import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ordersAPI } from '../services/api';
import { formatCurrency } from '../utils/format';
import { customToast, toastMessages } from '../utils/toast';
import { useLanguage } from '../contexts/LanguageContext';
import { LimitGate } from '../components/FeatureGate';
import {
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  Eye,
  DollarSign,
  ShoppingCart,
  Sparkles,
  Search,
  Filter,
  TrendingUp,
  Users,
  Package,
  AlertCircle,
  RefreshCw,
  MessageSquare,
  Ban
} from 'lucide-react';

const OrderManagement = () => {
  const { t } = useLanguage();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    orderId: null,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const queryClient = useQueryClient();

  // Fetch orders - improved error handling and data transformation  
  const { data: ordersData, isLoading, refetch, error } = useQuery(
    'orders',
    () => ordersAPI.getOrders(),
    {
      refetchInterval: 5000,
      onError: (error) => {
        console.error('Error fetching orders:', error);
        customToast.error(toastMessages.orderLoadError);
      }
    }
  );

  const orderss = ordersData?.data || [];

  // Add a function to map API status to translation keys
  const getStatusTranslationKey = (status) => {
    const statusMap = {
      'pending': 'pending',
      'in_progress': 'inProgress', // Map API's in_progress to JSON's inProgress
      'completed': 'completed',
      'cancelled': 'cancelled'
    };
    return `orders.status.${statusMap[status] || status}`;
  };

  // Transform API data to match frontend expectations - more robust
  const orders = React.useMemo(() => {
    if (!ordersData) return [];

    // Handle different API response structures
    const data = ordersData.data || ordersData;

    if (!Array.isArray(data)) {
      console.error('Expected array of orders, got:', data);
      return [];
    }

    return data.map((order, index) => {
      // Debug logging to see what's in the order object
      console.log('Order data:', order);

      // Try multiple possible ID fields with fallback
      const orderId = order.id || order.order_id || order.orderId || `ORD-${Date.now()}-${index}`;

      return {
        id: orderId,
        table_number: order.table_number || order.table_id || order.tableNumber || 'N/A',
        status: order.status || 'pending',
        payment_status: order.payment_status || order.paymentStatus || 'unpaid',
        payment_method: order.payment_method || order.paymentMethod,
        total_amount: order.total_amount || order.total || order.totalAmount || 0,
        created_at: order.created_at || order.order_date || order.createdAt || new Date().toISOString(),
        restaurant_id: order.restaurant_id || order.restaurantId,
        items: order.items || order.order_items || [],
        // Include customizations at order level if needed
        customizations: order.customizations || order.special_instructions || ''
      };
    });
  }, [ordersData]);

  // Debug: Log the first order to see its structure
  useEffect(() => {
    if (orders.length > 0) {
      console.log('First order data:', orders[0]);
      console.log('All orders:', orders);
    }
  }, [orders]);



  const updateStatusMutation = useMutation(
    async (data) => {
      console.log('Sending update request:', data);
      const response = await ordersAPI.updateOrderStatus(data);
      console.log('Update response:', response);
      return response;
    },
    {
      onMutate: async (data) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries('orders');

        // Snapshot the previous value
        const previousOrders = queryClient.getQueryData('orders');

        // Optimistically update to the new value
        queryClient.setQueryData('orders', (old) => {
          if (!old) return old;

          const dataToUpdate = old.data || old;
          if (!Array.isArray(dataToUpdate)) return old;

          return {
            ...old,
            data: dataToUpdate.map(order =>
              (order.id === data.id || order.order_id === data.id)
                ? { ...order, status: data.status }
                : order
            )
          };
        });

        // Return a context object with the snapshotted value
        return { previousOrders };
      },
      onError: (error, variables, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousOrders) {
          queryClient.setQueryData('orders', context.previousOrders);
        }
        console.error('Mutation error:', error);
        customToast.error(toastMessages.updateError('order status'));
      },
      onSuccess: (response) => {
        console.log('Mutation success, response:', response);
        // Force refresh to ensure we have the latest data from server
        queryClient.invalidateQueries('orders');
      },
    }
  );

  const updatePaymentMutation = useMutation(
    (data) => ordersAPI.updatePaymentStatus(data),
    {
      onMutate: async (data) => {
        await queryClient.cancelQueries('orders');
        const previousOrders = queryClient.getQueryData('orders');

        queryClient.setQueryData('orders', (old) => {
          if (!old) return old;

          const dataToUpdate = old.data || old;
          if (!Array.isArray(dataToUpdate)) return old;

          return {
            ...old,
            data: dataToUpdate.map(order =>
              (order.id === data.id || order.order_id === data.id)
                ? {
                  ...order,
                  payment_status: data.payment_status,
                  payment_method: data.payment_method
                }
                : order
            )
          };
        });

        return { previousOrders };
      },
      onError: (error, variables, context) => {
        if (context?.previousOrders) {
          queryClient.setQueryData('orders', context.previousOrders);
        }
        console.error('Update payment error:', error);
        customToast.error(toastMessages.updateError('payment status'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        customToast.success(toastMessages.paymentUpdated);
      },
    }
  );

  const cancelOrderMutation = useMutation(
    (orderId) => ordersAPI.cancelOrder(orderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        customToast.success(toastMessages.orderCancelled);
      },
      onError: (error) => {
        console.error('Cancel order error:', error);
        customToast.error(toastMessages.updateError('order cancellation'));
      },
    }
  );

  const handleStatusUpdate = async (orderId, status) => {
    console.log('Sending update:', { id: orderId, status: status });

    try {
      await updateStatusMutation.mutateAsync({
        id: orderId,
        status: status
      });
      // Close modal immediately for better UX
      if (selectedOrder) {
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Status update failed:', error);
    }
  };

  const handlePaymentUpdate = async (orderId, paymentStatus, paymentMethod) => {
    console.log('Sending payment update:', {
      id: orderId,
      payment_status: paymentStatus,
      payment_method: paymentMethod
    });

    try {
      await updatePaymentMutation.mutateAsync({
        id: orderId,
        payment_status: paymentStatus,
        payment_method: paymentMethod
      });
      // Close modal immediately for better UX
      if (selectedOrder) {
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Payment update failed:', error);
    }
  };

  const handleCancelOrder = async (orderId) => {
    // Show confirmation modal instead of window.confirm
    setConfirmationModal({
      isOpen: true,
      orderId: orderId,
      title: t('orders.cancelConfirmation.title'),
      message: t('orders.cancelConfirmation.message'),
      type: 'danger',
      onConfirm: async () => {
        try {
          await updateStatusMutation.mutateAsync({
            id: orderId,
            status: 'cancelled'
          });

          // Refresh data
          await queryClient.invalidateQueries('orders');
          await refetch();

          // Close modals
          setSelectedOrder(null);
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));

          customToast.success(toastMessages.orderCancelled);
        } catch (error) {
          console.error('Cancel order failed:', error);
          customToast.error(toastMessages.updateError('order cancellation'));
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  // Function to close confirmation modal
  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
  };

  // Filter orders based on status and search
  const filteredOrders = orders.filter(order => {
    const matchesStatus = selectedStatus === 'all' || order.status === selectedStatus;
    const matchesSearch = order.id.toString().includes(searchTerm) ||
      order.table_number.toString().includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <RefreshCw className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100 border-green-200';
      case 'unpaid': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const statusCounts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    in_progress: orders.filter(o => o.status === 'in_progress').length,
    completed: orders.filter(o => o.status === 'completed').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  const stats = [
    {
      icon: ShoppingCart,
      label: t('dashboard.stats.totalOrders'),
      value: orders.length,
      color: 'blue'
    },
    {
      icon: Clock,
      label: t('orders.status.pending'),
      value: statusCounts.pending,
      color: 'yellow'
    },
    {
      icon: TrendingUp,
      label: t('orders.status.inProgress'),
      value: statusCounts.in_progress,
      color: 'blue'
    },
    {
      icon: DollarSign,
      label: t('dashboard.stats.revenue'),
      value: formatCurrency(
        orders
          .filter(o => o.payment_status === 'paid')
          .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0)
      ),
      color: 'green'
    }
  ];

  // Status filter options with translations - FIXED
  const statusFilters = [
    { key: 'all', label: t('common.filter.all'), count: statusCounts.all },
    { key: 'pending', label: t('orders.status.pending'), count: statusCounts.pending },
    { key: 'in_progress', label: t('orders.status.inProgress'), count: statusCounts.in_progress }, // Use inProgress translation for in_progress filter
    { key: 'completed', label: t('orders.status.completed'), count: statusCounts.completed },
    { key: 'cancelled', label: t('orders.status.cancelled'), count: statusCounts.cancelled },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-5">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className={`mb-6 md:mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              {t('orders.title')}
            </div>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-3">{t('orders.title')}</h1>
          <p className="text-gray-600 text-sm md:text-lg max-w-2xl">
            {t('orders.subtitle')}
          </p>
        </div>

        {/* Stats Overview */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'from-blue-500 to-cyan-500',
              yellow: 'from-yellow-500 to-amber-500',
              green: 'from-green-500 to-emerald-500'
            };
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className={`p-2 md:p-3 rounded-xl bg-gradient-to-r ${colorClasses[stat.color]} text-white group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                  <div className="text-xs md:text-sm text-green-600 font-medium">
                    {stat.change}
                  </div>
                </div>

                <div>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-xs md:text-sm font-medium text-gray-600">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters and Actions */}
        <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6 md:mb-8 transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Status Filter */}
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((status) => (
                <button
                  key={status.key}
                  onClick={() => setSelectedStatus(status.key)}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap ${selectedStatus === status.key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                >
                  {status.label}
                  <span className={`ml-1 md:ml-2 px-1 md:px-1.5 py-0.5 rounded-full text-xs ${selectedStatus === status.key ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                    {status.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search and Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('common.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                />
              </div>

              <div className="flex items-center gap-2">
                <button className="flex items-center gap-2 bg-gray-50 px-3 md:px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-all duration-300">
                  <Filter className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium hidden sm:inline">{t('common.filter.title')}</span>
                </button>

                <button
                  onClick={() => refetch()}
                  className="flex items-center gap-2 bg-blue-600 text-white px-3 md:px-4 py-2 rounded-xl hover:bg-blue-700 transition-all duration-300 hover:scale-105"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span className="text-sm font-medium">{t('common.refresh')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <LimitGate
          type="orders"
          current={filteredOrders.length}
        >
          {isLoading ? (
            <div className="flex justify-center py-8 md:py-12">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 md:py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
                <ShoppingCart className="h-8 w-8 md:h-10 md:w-10 text-blue-600" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">{t('orders.empty.title')}</h3>
              <p className="text-gray-600 mb-6 md:mb-8 max-w-md mx-auto text-sm md:text-base">
                {searchTerm || selectedStatus !== 'all'
                  ? t('orders.empty.filtered')
                  : t('orders.empty.default')
                }
              </p>
            </div>
          ) : (
            <div className={`space-y-3 md:space-y-4 transition-all duration-700 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border border-gray-100"
                >
                  <div className="p-4 md:p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                        <div className={`p-2 md:p-3 rounded-xl border ${getStatusColor(order.status)} flex-shrink-0`}>
                          {getStatusIcon(order.status)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate">
                              {t('orders.orderNumber')} #{order.id}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)} whitespace-nowrap`}>
                                {t(getStatusTranslationKey(order.status))}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(order.payment_status)} whitespace-nowrap`}>
                                {t(`orders.paymentStatus.${order.payment_status}`)}
                              </span>
                            </div>
                          </div>

                          <p className="text-xs md:text-sm text-gray-600 truncate">
                            {t('dashboard.recentOrders.tableInfo', 'Table {table} • {amount}')
                              .replace('{table}', order.table_number)
                              .replace('{amount}', formatCurrency(order.total_amount))
                            }
                          </p>

                          <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {t('orders.customerOrder')}
                            </span>
                            <span className="flex items-center gap-1 font-semibold text-green-600">
                              {formatCurrency(order.total_amount)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="bg-blue-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-blue-700 transition-all duration-300 hover:scale-105 flex items-center gap-1 md:gap-2 text-sm"
                        >
                          <Eye className="h-3 w-3 md:h-4 md:w-4" />
                          <span className="hidden xs:inline">{t('common.view')}</span>
                        </button>

                        {/* Cancel Order Button - Show for pending and in_progress orders */}
                        {(order.status === 'pending' || order.status === 'in_progress') && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            className="bg-red-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-red-700 transition-all duration-300 hover:scale-105 flex items-center gap-1 md:gap-2 text-sm whitespace-nowrap"
                            disabled={cancelOrderMutation.isLoading}
                          >
                            <Ban className="h-3 w-3 md:h-4 md:w-4" />
                            <span className="hidden xs:inline">{t('common.cancel')}</span>
                          </button>
                        )}

                        {order.status === 'pending' && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'in_progress')}
                            className="bg-yellow-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-yellow-700 transition-all duration-300 hover:scale-105 text-sm whitespace-nowrap"
                            disabled={updateStatusMutation.isLoading}
                          >
                            {t('orders.actions.start')}
                          </button>
                        )}

                        {order.status === 'in_progress' && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'completed')}
                            className="bg-green-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-green-700 transition-all duration-300 hover:scale-105 text-sm whitespace-nowrap"
                            disabled={updateStatusMutation.isLoading}
                          >
                            {t('orders.actions.complete')}
                          </button>
                        )}

                        {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handlePaymentUpdate(order.id, 'paid', 'cash')}
                            className="bg-green-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-green-700 transition-all duration-300 hover:scale-105 flex items-center gap-1 md:gap-2 text-sm whitespace-nowrap"
                            disabled={updatePaymentMutation.isLoading}
                          >
                            <CreditCard className="h-3 w-3 md:h-4 md:w-4" />
                            <span className="hidden xs:inline">{t('orders.actions.pay')}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </LimitGate>

        {/* Order Details Modal */}
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onStatusUpdate={handleStatusUpdate}
            onPaymentUpdate={handlePaymentUpdate}
            onCancelOrder={handleCancelOrder}
            isLoading={updateStatusMutation.isLoading || updatePaymentMutation.isLoading || cancelOrderMutation.isLoading}
            isUpdatingStatus={updateStatusMutation.isLoading}
            isUpdatingPayment={updatePaymentMutation.isLoading}
            isCancelling={cancelOrderMutation.isLoading}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={closeConfirmationModal}
          onConfirm={confirmationModal.onConfirm}
          title={confirmationModal.title}
          message={confirmationModal.message}
          type={confirmationModal.type}
          confirmText={t('orders.cancelConfirmation.confirmText')}
          cancelText={t('orders.cancelConfirmation.cancelText')}
        />
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning"
}) => {
  if (!isOpen) return null;

  const typeConfig = {
    warning: {
      icon: <AlertCircle className="h-6 w-6 text-yellow-600" />,
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      buttonColor: "bg-yellow-600 hover:bg-yellow-700"
    },
    danger: {
      icon: <Ban className="h-6 w-6 text-red-600" />,
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      buttonColor: "bg-red-600 hover:bg-red-700"
    },
    info: {
      icon: <AlertCircle className="h-6 w-6 text-blue-600" />,
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      buttonColor: "bg-blue-600 hover:bg-blue-700"
    }
  };

  const config = typeConfig[type];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 transform transition-all duration-300 scale-100">
        <div className={`flex items-start gap-4 mb-4 p-4 rounded-xl ${config.bgColor} border ${config.borderColor}`}>
          <div className="flex-shrink-0">
            {config.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 border border-gray-200 hover:scale-105"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-300 hover:scale-105 ${config.buttonColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Order Details Modal Component with customizations
const OrderDetailsModal = ({ order,
  onClose,
  onStatusUpdate,
  onPaymentUpdate,
  onCancelOrder,
  isLoading,
  isUpdatingStatus,
  isUpdatingPayment,
  isCancelling }) => {
  const { t } = useLanguage();
  const [orderItems, setOrderItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    if (order) {
      setLoadingItems(true);
      ordersAPI.getOrderById(order.id)
        .then(response => {
          const orderData = response.data || response;
          setOrderItems(orderData.items || orderData.order_items || []);
        })
        .catch(error => {
          console.error('Failed to fetch order items:', error);
          customToast.error(toastMessages.updateError('order details'));
        })
        .finally(() => {
          setLoadingItems(false);
        });
    }
  }, [order]);

  const getStatusTranslationKey = (status) => {
    const statusMap = {
      'pending': 'pending',
      'in_progress': 'inProgress', // Map API's in_progress to JSON's inProgress
      'completed': 'completed',
      'cancelled': 'cancelled'
    };
    return `orders.status.${statusMap[status] || status}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100 border-green-200';
      case 'unpaid': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const totalItems = orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-4 md:p-6 lg:p-8 w-full max-w-4xl max-h-screen overflow-y-auto shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900">
              {t('orders.details.title', { number: order.id })}
            </h3>
            <p className="text-gray-600 mt-1 text-sm md:text-base">{t('orders.details.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 md:p-2 hover:bg-gray-100 rounded-xl transition-colors duration-300"
          >
            <XCircle className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
          </button>
        </div>

        {/* Order Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.tableNumber')}</p>
            <p className="font-bold text-gray-900 text-base md:text-lg">{t('orders.details.tableValue')} {order.table_number}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.orderTime')}</p>
            <p className="font-bold text-gray-900 text-base md:text-lg">{new Date(order.created_at).toLocaleTimeString()}</p>
            <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.status')}</p>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)} whitespace-nowrap`}>
              {t(getStatusTranslationKey(order.status))}
            </span>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.payment')}</p>
            <span className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium border ${getPaymentStatusColor(order.payment_status)}`}>
              {t(`orders.paymentStatus.${order.payment_status}`)}
            </span>
          </div>
        </div>

        {/* Order Items with Customizations */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 md:mb-4 gap-2">
            <h4 className="text-lg md:text-xl font-bold text-gray-900">
              {t('orders.details.itemsTitle')} : {totalItems}
            </h4>
            {order.payment_method && (
              <p className="text-xs md:text-sm text-gray-600">
                {t('orders.details.paymentMethod')}: <span className="font-medium capitalize">{order.payment_method}</span>
              </p>
            )}
          </div>

          {loadingItems ? (
            <div className="flex justify-center py-6 md:py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : orderItems.length === 0 ? (
            <div className="text-center py-6 md:py-8 bg-gray-50 rounded-xl border border-gray-200">
              <Package className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-3 text-gray-400" />
              <p className="text-gray-500 text-sm md:text-base">{t('orders.details.noItems')}</p>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {orderItems.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors duration-300 overflow-hidden">
                  <div className="p-3 md:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 md:mb-3 gap-2">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <h5 className="font-semibold text-gray-900 text-base md:text-lg">{item.dish_name || item.name}</h5>
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs md:text-sm font-medium self-start sm:self-center">
                            {t('orders.details.quantity')} {item.quantity}
                          </span>
                        </div>

                        {/* Customizations Display */}
                        {item.customizations && item.customizations.trim() !== '' && (
                          <div className="mt-2 p-2 md:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <MessageSquare className="h-3 w-3 md:h-4 md:w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs md:text-sm font-medium text-yellow-800 mb-1">{t('orders.details.specialInstructions')}</p>
                                <p className="text-yellow-700 text-xs md:text-sm">{item.customizations}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="font-bold text-green-600 text-base md:text-lg">
                          CHF {parseFloat(item.price || item.unit_price || 0).toFixed(2)}
                        </p>
                        <p className="text-xs md:text-sm text-gray-600">
                          CHF {(parseFloat(item.price || item.unit_price || 0) * item.quantity).toFixed(2)} {t('orders.details.total')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order-level Customizations */}
        {order.customizations && order.customizations.trim() !== '' && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-2 md:gap-3">
              <MessageSquare className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-800 mb-1 text-sm md:text-base">{t('orders.details.orderInstructions')}</p>
                <p className="text-blue-700 text-sm md:text-base">{order.customizations}</p>
              </div>
            </div>
          </div>
        )}

        {/* Total and Actions */}
        <div className="border-t pt-4 md:pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-2">
            <div>
              <p className="text-base md:text-lg font-semibold text-gray-900">{t('orders.details.orderTotal')}</p>
              <p className="text-xs md:text-sm text-gray-600">{t('orders.details.orderTotalDescription')}</p>
            </div>
            <span className="text-xl md:text-2xl font-bold text-green-600">
              {formatCurrency(order.total_amount)}
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            {/* Cancel Order Button - Show for pending and in_progress orders */}
            {(order.status === 'pending' || order.status === 'in_progress') && order.status !== 'cancelled' && (
              <button
                onClick={() => onCancelOrder(order.id)}
                className="flex-1 bg-red-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-red-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isCancelling}
              >
                <Ban className="h-4 w-4 md:h-5 md:w-5" />
                {isCancelling ? t('common.loading') : t('orders.actions.cancelOrder')}
              </button>
            )}

            {order.status === 'pending' && order.status !== 'cancelled' && (
              <button
                onClick={() => onStatusUpdate(order.id, 'in_progress')}
                className="flex-1 bg-yellow-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-yellow-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isUpdatingStatus}
              >
                <RefreshCw className="h-4 w-4 md:h-5 md:w-5" />
                {isUpdatingStatus ? t('common.loading') : t('orders.actions.startPreparing')}
              </button>
            )}

            {order.status === 'in_progress' && order.status !== 'cancelled' && (
              <button
                onClick={() => onStatusUpdate(order.id, 'completed')}
                className="flex-1 bg-green-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-green-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isUpdatingStatus}
              >
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5" />
                {isUpdatingStatus ? t('common.loading') : t('orders.actions.markCompleted')}
              </button>
            )}

            {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
              <button
                onClick={() => onPaymentUpdate(order.id, 'paid', 'cash')}
                className="flex-1 bg-green-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-green-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isUpdatingPayment}
              >
                <CreditCard className="h-4 w-4 md:h-5 md:w-5" />
                {isUpdatingPayment ? t('common.loading') : t('orders.actions.markPaid')}
              </button>
            )}

            <button
              onClick={onClose}
              className="py-2.5  md:py-3 px-4 md:px-6 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm md:text-base"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;